import { makeAutoObservable } from 'mobx';
import { IAttributeTextData } from '@canvas-logic/clp-domain';
import { IUICfgAttributeText, IUICfgAttributeBoolean, IUICfgAttributeInteger } from '@canvas-logic/clp-api-types';

// ============================================================================
// Mock Text Attribute State
// ============================================================================

export interface MockTextAttributeConfig {
  id: string;
  initialValue: string;
  availableValues: string[];
  viewElement: IUICfgAttributeText;
  textData: IAttributeTextData;
}

export class MockTextAttributeState {
  elementKind = 'attribute';
  elementType = 'text';

  id: string;
  visible = true;
  value: string | null;
  availableValues: string[];
  loading = false;
  viewElement: IUICfgAttributeText;
  textData: IAttributeTextData;
  lookup: { current: { value: { value: string } } | null };

  constructor(config: MockTextAttributeConfig) {
    this.id = config.id;
    this.value = config.initialValue;
    this.availableValues = config.availableValues;
    this.viewElement = config.viewElement;
    this.textData = config.textData;
    this.lookup = {
      current: config.initialValue ? { value: { value: config.initialValue } } : null,
    };
    makeAutoObservable(this);
  }

  setValue = (newValue: string) => {
    this.value = newValue;
    this.lookup.current = { value: { value: newValue } };
  };

  get selectedTextData() {
    return this.textData.values.find((v) => v.value === this.value);
  }
}

// ============================================================================
// Mock Boolean Attribute State
// ============================================================================

export interface MockBooleanAttributeConfig {
  id: string;
  initialValue: boolean;
  availableValues: boolean[];
  viewElement: IUICfgAttributeBoolean;
  textData: IAttributeTextData;
}

export class MockBooleanAttributeState {
  elementKind = 'attribute';
  elementType = 'boolean';

  id: string;
  visible = true;
  value: boolean | null;
  availableValues: boolean[];
  loading = false;
  viewElement: IUICfgAttributeBoolean;
  textData: IAttributeTextData;
  lookup: { current: { value: { value: boolean } } | null };

  constructor(config: MockBooleanAttributeConfig) {
    this.id = config.id;
    this.value = config.initialValue;
    this.availableValues = config.availableValues;
    this.viewElement = config.viewElement;
    this.textData = config.textData;
    this.lookup = {
      current: config.initialValue !== null ? { value: { value: config.initialValue } } : null,
    };
    makeAutoObservable(this);
  }

  setValue = (newValue: boolean) => {
    this.value = newValue;
    this.lookup.current = { value: { value: newValue } };
  };

  get selectedTextData() {
    return this.textData.values.find((v) => v.value === this.value);
  }
}

// ============================================================================
// Mock Integer Attribute State
// ============================================================================

export interface MockIntegerAttributeConfig {
  id: string;
  initialValue: number;
  availableValues: number[];
  viewElement: IUICfgAttributeInteger;
  textData: IAttributeTextData;
}

export class MockIntegerAttributeState {
  elementKind = 'attribute';
  elementType = 'integer';

  id: string;
  visible = true;
  value: number | null;
  availableValues: number[];
  loading = false;
  viewElement: IUICfgAttributeInteger;
  textData: IAttributeTextData;
  lookup: { current: { value: { value: number } } | null };

  constructor(config: MockIntegerAttributeConfig) {
    this.id = config.id;
    this.value = config.initialValue;
    this.availableValues = config.availableValues;
    this.viewElement = config.viewElement;
    this.textData = config.textData;
    this.lookup = {
      current: config.initialValue !== null ? { value: { value: config.initialValue } } : null,
    };
    makeAutoObservable(this);
  }

  setValue = (newValue: number) => {
    this.value = newValue;
    this.lookup.current = { value: { value: newValue } };
  };

  get selectedTextData() {
    return this.textData.values.find((v) => v.value === this.value);
  }
}
